/**
 * Identita → „Z modulů“: entity, které zakládají moduly (owner_module),
 * seskupené podle vlastníka. Správu deleguje na modul přes MCP kontrakt
 * v manifestu (entities[].manage_tool) — Filip: „správu a volání má modul
 * standardizovat MCP do té míry, že to má fungovat univerzálně“.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Boxes,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Wrench,
} from "lucide-react";
import { mcpCall } from "../lib/mcp";
import { AsyncStatusToggle } from "../components/ui";

interface Kind {
  type: string;
  label: string;
  description?: string;
  manage_tool?: string;
}

interface Row {
  id: string;
  type: string;
  label: string;
  external_id: string | null;
  enabled: boolean;
  protected: boolean;
  api_keys_count: number;
  metadata: Record<string, unknown>;
  updated_at: string;
}

interface Group {
  module: string;
  label: string;
  loaded: boolean;
  kinds: Kind[];
  entities: Row[];
  undeclared_types: string[];
}

interface Resp {
  ok: boolean;
  groups: Group[];
  total: number;
}

export function IdentityModuleEntitiesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const q = useQuery({
    queryKey: ["identity-module-entities"],
    queryFn: () => mcpCall<Resp>("identity.module_entities"),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      mcpCall("identity.entity_update", { id, enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["identity-module-entities"] });
      void qc.invalidateQueries({ queryKey: ["entities"] });
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });

  const groups = q.data?.groups ?? [];
  const isOpen = (m: string) => open[m] ?? true;

  return (
    <div className="space-y-3">
      <p className="text-[12px] text-gray-500 leading-snug">
        {t(
          "entities.modulesIntro",
          "Entity založené moduly (uzly meshe, fleet klienti, projektové entity, účelové tokeny). Vlastník je modul; správu poskytuje jeho MCP tool. Lidské účty jsou v záložce Entity.",
        )}
      </p>
      {q.isLoading && (
        <div className="text-sm text-gray-500">
          {t("common.loading", "Načítám…")}
        </div>
      )}
      {q.isError && (
        <div className="text-sm text-red-400">
          {String((q.error as Error)?.message ?? q.error)}
        </div>
      )}
      {!q.isLoading && groups.length === 0 && (
        <div className="text-sm text-gray-500">
          {t("entities.modulesEmpty", "Žádné entity z modulů.")}
        </div>
      )}
      {groups.map((g) => (
        <div
          key={g.module}
          className="rounded-xl border border-gray-800 bg-gray-900/40"
        >
          <button
            type="button"
            onClick={() =>
              setOpen((o) => ({ ...o, [g.module]: !isOpen(g.module) }))}
            className="flex w-full items-center gap-3 px-4 py-3 text-left"
          >
            {isOpen(g.module)
              ? <ChevronDown size={14} className="text-gray-500 shrink-0" />
              : <ChevronRight size={14} className="text-gray-500 shrink-0" />}
            <Boxes size={15} className="text-blue-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white">
                {g.label}{" "}
                <span className="text-gray-500 font-normal">· {g.module}</span>
              </div>
              <div className="text-[11px] text-gray-500">
                {t("entities.modulesCount", "{{count}} entit", {
                  count: g.entities.length,
                })}
                {g.kinds.length > 0 &&
                  ` · ${g.kinds.map((k) => k.label).join(", ")}`}
                {!g.loaded &&
                  ` · ${t("entities.modulesNotLoaded", "modul není načtený")}`}
              </div>
              {g.undeclared_types.length > 0 && (
                <div className="mt-0.5 text-[11px] text-amber-300/90">
                  {t(
                    "entities.modulesUndeclared",
                    "Bez kontraktu v manifestu: {{types}}",
                    { types: g.undeclared_types.join(", ") },
                  )}
                </div>
              )}
            </div>
            <Link
              to={`/modules/${encodeURIComponent(g.module)}`}
              onClick={(e) => e.stopPropagation()}
              className="text-gray-500 hover:text-blue-400 shrink-0"
              title={t("entities.modulesOpenModule", "Otevřít modul")}
            >
              <ExternalLink size={14} />
            </Link>
          </button>
          {isOpen(g.module) && (
            <div className="border-t border-gray-800/60 divide-y divide-gray-800/60">
              {g.entities.length === 0 && (
                <div className="px-4 py-2 text-[12px] text-gray-600">
                  {t("entities.modulesNone", "zatím žádné")}
                </div>
              )}
              {g.entities.map((e) => {
                const kind = g.kinds.find((k) => k.type === e.type);
                return (
                  <div key={e.id} className="flex items-center gap-3 px-4 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white truncate">
                          {e.label}
                        </span>
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 border border-gray-800 rounded px-1.5 py-0.5">
                          {kind?.label ?? e.type}
                        </span>
                        {e.protected && (
                          <span className="text-[10px] text-amber-300/80">
                            {t("entities.protected", "chráněná")}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-gray-500 truncate">
                        {e.external_id ?? e.id}
                        {e.api_keys_count > 0 &&
                          ` · ${
                            t("entities.modulesKeys", "{{count}} klíčů", {
                              count: e.api_keys_count,
                            })
                          }`}
                      </div>
                    </div>
                    {kind?.manage_tool && (
                      <Link
                        to={`/tools?tool=${
                          encodeURIComponent(kind.manage_tool)
                        }`}
                        className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-blue-400 shrink-0"
                        title={kind.manage_tool}
                      >
                        <Wrench size={12} />{" "}
                        {t("entities.modulesManage", "Spravovat")}
                      </Link>
                    )}
                    <AsyncStatusToggle
                      value={e.enabled}
                      actual={e.enabled}
                      pending={toggleMut.isPending &&
                        toggleMut.variables?.id === e.id}
                      disabled={e.protected}
                      onToggle={(v) =>
                        toggleMut.mutateAsync({ id: e.id, enabled: v })}
                      onError={(err) =>
                        toast.error(String((err as Error)?.message ?? err))}
                      className="shrink-0"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
